import { useEffect, useRef } from "react";
import { InteractionStatus } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Navigate, useLocation } from "react-router-dom";
import { loginRequest } from "@/features/auth/authConfig";
import { useUpdateLastLoginDate, useUserByEmail } from "@/services/settings";
import { AccessDeniedPage } from "@/features/auth/AccessDeniedPage";

interface ProtectedRouteProps {
  children: ReactNode;
}

const menuPermissionMap = [
  { path: "/home", permission: null },
  { path: "/dashboard", permission: ["CanViewDashboard", "CanViewGRCDashboard"] },
  { path: "/grcdashboard", permission: "CanViewGRCReport" },
  { path: "/draftcalendar", permission: "CanViewDraftCalender" },
  { path: "/calendarscheduleview", permission: "CanViewDraftCalender" },
  { path: "/settings", permission: "CanViewSettings" },
  { path: "/message-monitor", permission: "CanViewMessageLog" },
] as const;

function hasPermission(userPermissions: string[], permission: string | readonly string[]): boolean {
  if (typeof permission === "string") return userPermissions.includes(permission);
  return permission.some((p) => userPermissions.includes(p));
}

function requiredPermissionForPath(pathname: string): string | readonly string[] | null {
  if (pathname === "/") return null;

  const menu = menuPermissionMap.find((item) =>
    pathname === item.path || pathname.startsWith(`${item.path}/`)
  );

  return menu?.permission ?? null;
}

function firstAllowedPath(userPermissions: string[]): string | null {
  const firstAllowed = menuPermissionMap.find((item) =>
    item.permission === null || hasPermission(userPermissions, item.permission)
  );

  return firstAllowed?.path ?? null;
}

/**
 * Mirrors Blazor LandingPage.razor:
 * 1. Ensures MSAL authentication (redirects to login if not signed in).
 * 2. Fetches the signed-in user from the API by email — once, cached for the session.
 * 3. If the user is not found or has no role assigned → shows AccessDeniedPage.
 * 4. Only renders children when the user is fully authorized.
 *
 * Because useUserByEmail uses staleTime: Infinity, the API call is made exactly
 * once per browser session; every useCurrentUser() / usePermissions() call across
 * the app reads from the same TanStack Query cache entry — no repeated fetches.
 */
export function ProtectedRoute({ children }: Readonly<ProtectedRouteProps>) {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress, accounts } = useMsal();
  const location = useLocation();
  const email = accounts[0]?.username ?? "";
  const { mutate: updateLastLoginDate } = useUpdateLastLoginDate();

  // Once the API returns 401/403, stop re-fetching — the user is permanently denied.
  const accessDeniedRef = useRef(false);
  const updatedLastLoginForEmailRef = useRef<string | null>(null);
  const updateLastLoginDateRef = useRef(updateLastLoginDate);
  updateLastLoginDateRef.current = updateLastLoginDate;

  // Fetch user profile. Enabled only after MSAL auth is complete and we have an email.
  // Disabled permanently once a 401/403 is received to prevent infinite re-fetch loops.
  const { data: user, isPending: isUserPending, isError, error } = useUserByEmail(email, {
    enabled: !accessDeniedRef.current && isAuthenticated && inProgress === InteractionStatus.None && Boolean(email),
  });

  // Mark as permanently denied so the query is never re-enabled.
  if (isError && error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    if ((status === 401 || status === 403) && !accessDeniedRef.current) {
      accessDeniedRef.current = true;
    }
  }

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      void instance.loginRedirect(loginRequest);
    }
  }, [instance, isAuthenticated, inProgress]);

  useEffect(() => {
    if (!isAuthenticated || inProgress !== InteractionStatus.None) return;
    if (!email || !user || isError) return;
    if (updatedLastLoginForEmailRef.current === email) return;

    updatedLastLoginForEmailRef.current = email;
    updateLastLoginDateRef.current(email);
  }, [email, inProgress, isAuthenticated, isError, user]);

  // Still doing MSAL redirect / SSO
  if (inProgress !== InteractionStatus.None) {
    return <AuthSpinner message="Signing in…" />;
  }

  if (!isAuthenticated) {
    return <AuthSpinner message="Redirecting to sign-in…" />;
  }

  // API returned 401/403 → authorization filter denied access → show access denied.
  if (isError && error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    if (status === 401 || status === 403) {
      return <AccessDeniedPage />;
    }
  }

  // Fetching user profile from API
  if (isUserPending) {
    return <AuthSpinner message="Authorizing…" />;
  }

  // User not in system → access denied
  // Mirrors: if (userdata == null) NavigateTo("access_denied")
  if (!isError && (!user)) {
    return <AccessDeniedPage />;
  }

  // Distinguish other API/transient failures.
  if (isError) {
    return <AuthSpinner message="Unable to verify access. Please try again." />;
  }

  const userPermissions = user.permissions ?? [];
  const requiredPermission = requiredPermissionForPath(location.pathname);

  if (requiredPermission && !hasPermission(userPermissions, requiredPermission)) {
    const fallbackPath = firstAllowedPath(userPermissions);

    if (!fallbackPath) {
      return <AccessDeniedPage />;
    }

    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}

function AuthSpinner({ message }: Readonly<{ message: string }>) {
  return (
    <div className="flex min-h-screen items-center justify-center gap-3 text-slate-600">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span className="text-sm">{message}</span>
    </div>
  );
}

